import React, { useState } from 'react';
import { Button } from './ui/button';
import { Compass, Palette, MessageSquare, Megaphone, BarChart3, ArrowRight, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ContactModal from './ContactModal';

const iconComponents = [Compass, Palette, MessageSquare, Megaphone, BarChart3];

const ServiceCard = ({ service, index }) => {
  const IconComponent = iconComponents[index];

  return (
    <div className="service-card card-hover bg-white rounded-xl sm:rounded-2xl p-3 sm:p-6 border border-slate-100 shadow-sm">
      {/* Icon */}
      <div className="icon-badge icon-glow w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-5">
        <IconComponent className="w-4 h-4 sm:w-6 sm:h-6 text-[#1a237e]" />
      </div>

      {/* Title & Outcome */}
      <h3 className="text-sm sm:text-xl font-bold text-[#0d1442] mb-0.5 sm:mb-1">{service.title}</h3>
      <p className="text-xs sm:text-sm font-medium text-[#5c6bc0] mb-2 sm:mb-4">{service.outcome}</p>

      {/* Bullets */}
      <ul className="space-y-1 sm:space-y-2 hidden sm:block">
        {service.bullets.map((bullet) => (
          <li key={bullet} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-[#4dd0e1] mt-0.5 flex-shrink-0" />
            <span className="text-sm text-slate-600">{bullet}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ServicesSection = () => {
  const { t } = useLanguage();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const services = t('services.items');

  return (
    <section id="services" className="py-16 bg-white relative">
      <div className="absolute inset-0 blueprint-grid opacity-30" />
      
      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0d1442] mb-4">
            {t('services.title')}<span className="gradient-text">{t('services.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-600">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Services Grid - minimum 2 columns */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 mb-12">
          {Array.isArray(services) && services.map((service, index) => (
            <ServiceCard key={service.title || index} service={service} index={index} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button 
            onClick={() => setIsContactOpen(true)}
            className="btn-gradient text-white px-8 py-4 rounded-lg text-base font-semibold h-auto"
            data-testid="services-contact-btn"
          >
            {t('services.cta')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactOpen} 
        onClose={() => setIsContactOpen(false)} 
      />
    </section>
  );
};

export default ServicesSection;
